import classNames from "classnames";
import Dialog from "rc-dialog";
import "rc-dialog/assets/index.css";
import { PureComponent, ReactNode } from "react";
import KuikaButton from "../kuika-button/kuika-button";
import KuikaCloseButton from "../kuika-close-button/kuika-close-button";
import { isKitPopupOpen } from "../kuika-drawer/open-kit-popup-selector";
import KuikaSpinnerSmall from "../kuika-spinner-small/kuika-spinner-small";
import KuikaSmallBoldText from "../typography/SmallBoldText/SmallBoldText";
import KModalStepBody, { KModalStepKey } from "./k-modal-step-body";
import "./k-modal.scss";

export interface IKModalState {
  isFullScreen: boolean;
}

export interface IKModalProps {
  children?: ReactNode;
  footer?: ReactNode;
  showModal?: boolean;
  closeModal?: () => void;
  header?: React.ReactNode;
  hideHeader?: boolean;
  hideCloseIcon?: boolean;
  /**
   * Shows the header's close button disabled — for a modal that will not close right now (a save in
   * flight), so the X says so instead of looking live and doing nothing.
   */
  closeDisabled?: boolean;
  width?: number | string;
  centered?: boolean;
  isLoading?: boolean;
  /**
   * Caption under the loading glyph. For a dialog whose wait is long enough to need saying
   * what it is doing — a per-step progress line, not a generic "Loading…".
   */
  loadingLabel?: string;
  bodyStyle?: React.CSSProperties;
  className?: string;
  dontRenderSpinner?: boolean;
  /** Start the modal filling the viewport. Externally controlled; defaults to false. */
  fullScreen?: boolean;
  /** Render a maximize/restore toggle in the header. Defaults to false. */
  showFullScreenButton?: boolean;
  /**
   * Overrides the close button's id. Only worth passing where a suite already targets a
   * different one — `DefaultModal` keeps the `closeModal` its consumers have always had.
   */
  closeTestId?: string;
  /**
   * Test id for rc-dialog's own wrap (`tabindex="-1"` div, `${prefixCls}-wrap`) — the element
   * that actually intercepts clicks on whatever is behind the modal, and that stays interactive
   * for the whole leave animation (`display` only flips off once it finishes; `destroyOnClose`
   * unmounts the CONTENT on its own, separate timeline). A caller whose next step reaches for
   * something behind this modal the instant it closes should wait on THIS test id going hidden,
   * not on a field inside `children`. Omit it and the wrap carries none, unchanged from before.
   */
  wrapTestId?: string;
  /**
   * Identity of the current step of a multi-step modal (a wizard whose body changes in place). When it
   * changes, the old step cross-fades out while the new one fades in, and the body morphs its height
   * from one to the other instead of jumping. Omit it for a single-view modal: the body renders as it
   * always has.
   */
  stepKey?: KModalStepKey;
}

export class KModal extends PureComponent<IKModalProps, IKModalState> {
  constructor(props: IKModalProps) {
    super(props);
    this.state = { isFullScreen: props.fullScreen ?? false };
  }

  handleCloseModal = (event?: React.SyntheticEvent): void => {
    // rc-dialog's own Escape handling (keyboard=true) calls this unconditionally, with no way to
    // ask it to stand down. An open kit popup (a select dropdown, a dropdown menu) should absorb
    // the Escape instead — closing it, not the modal underneath. Scoped to the keydown path only:
    // the header/footer close buttons (a "click" event, or none at all) always close the modal,
    // even if some unrelated popup elsewhere in the document happens to be open.
    if (event?.type === "keydown" && isKitPopupOpen(document)) {
      return;
    }
    const { closeModal } = this.props;
    if (closeModal) closeModal();
  };

  toggleFullScreen = (): void => {
    this.setState((prev: IKModalState): IKModalState => ({ isFullScreen: !prev.isFullScreen }));
  };

  render() {
    const {
      className,
      header,
      showModal,
      width,
      bodyStyle,
      centered = true,
      footer,
      children,
      dontRenderSpinner,
      isLoading,
      loadingLabel,
      hideHeader,
      hideCloseIcon,
      closeDisabled,
      showFullScreenButton,
      closeTestId,
      wrapTestId,
      stepKey
    } = this.props;

    const isFullScreen: boolean = this.state.isFullScreen;

    const closeButton: ReactNode = !hideCloseIcon && (
      <KuikaCloseButton
        data-testid={closeTestId ?? "closeModalButton"}
        data-onboarding-id="modal_close-button"
        disabled={closeDisabled}
        onClick={this.handleCloseModal}
      />
    );

    const fullScreenButton: ReactNode = showFullScreenButton && (
      <KuikaButton
        data-testid="fullScreenModalButton"
        color="neutral"
        type="tertiary"
        isIcon
        size="md"
        aria-label={isFullScreen ? "Restore" : "Full screen"}
        onClick={this.toggleFullScreen}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {isFullScreen ? (
            <>
              <path d="M8 3v3a2 2 0 0 1-2 2H3" />
              <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
              <path d="M3 16h3a2 2 0 0 1 2 2v3" />
              <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
            </>
          ) : (
            <>
              <path d="M8 3H5a2 2 0 0 0-2 2v3" />
              <path d="M16 3h3a2 2 0 0 1 2 2v3" />
              <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
              <path d="M16 21h3a2 2 0 0 1 2-2v-3" />
            </>
          )}
        </svg>
      </KuikaButton>
    );

    // Byte-identical to the legacy layout when showFullScreenButton is
    // omitted (close button stays a direct sibling of the header text).
    const title: ReactNode = showFullScreenButton ? (
      <>
        <KuikaSmallBoldText>{header}</KuikaSmallBoldText>
        <span className="k-modal-title-actions">
          {fullScreenButton}
          {closeButton}
        </span>
      </>
    ) : (
      <>
        <KuikaSmallBoldText>{header}</KuikaSmallBoldText>
        {closeButton}
      </>
    );

    const modalClassName = classNames(className, {
      "k-modal--hide-header": hideHeader,
      "k-modal--centered": centered,
      "k-modal--fullscreen": isFullScreen
    });
    const effectiveWidth: number | string = isFullScreen ? "100vw" : (width ?? 400);
    const body: ReactNode =
      stepKey === undefined ? children : <KModalStepBody stepKey={stepKey}>{children}</KModalStepBody>;
    return (
      <Dialog
        prefixCls="k-modal"
        transitionName="k-modal-motion"
        maskTransitionName="k-modal-mask-motion"
        destroyOnClose={true}
        wrapProps={wrapTestId ? { "data-testid": wrapTestId } : undefined}
        className={modalClassName}
        title={hideHeader ? null : title}
        visible={showModal}
        onClose={this.handleCloseModal}
        style={{ width: effectiveWidth }}
        styles={bodyStyle ? { body: bodyStyle } : undefined}
        maskClosable={false}
        footer={footer ?? null}
        closeIcon={null}
        keyboard={true}
      >
        {!dontRenderSpinner ? (
          <KuikaSpinnerSmall spinning={isLoading ?? false} size="small" label={loadingLabel}>
            {body}
          </KuikaSpinnerSmall>
        ) : (
          body
        )}
      </Dialog>
    );
  }
}
